import {Fragment, useCallback, useEffect, useRef, useState} from "react";
import styled from "@emotion/styled";
import {Topic} from "@mui/icons-material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import {Box, Divider, Drawer, Grid, IconButton, Tooltip, Typography} from "@mui/material";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import _ from "lodash";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {getCustomPageInternalUrl, getNodeDashboardsInternalUrl, getNodeInternalUrl} from "../../../constants/appRoutes";
import {ConfigNodeMenuId} from "../../../model/item-containers-models/configNodeMenuId";
import CategoriesTree from "../../categories-tree";
import CustomLink from "../../custom-link";
import DetailLevelSelect from "../../detail-level-select";
import ModulesPlaceholder from "../../modules-placeholder";
import {languageSelector} from "../../../state/app/appSelectors";
import {nodeCodeSelector} from "../../../state/node/nodeSelectors";
import {localizeI18nObj} from "../../../utils/i18n";
import {getCustomPagesInMenu} from "../../../utils/itemContainers";
import {showNodeDashboardsButton} from "../utils";

const Accordion = styled(MuiAccordion)({
  root: {
    boxShadow: "none",
    "&:not(:last-child)": {
      borderBottom: 0
    },
    "&:before": {
      display: "none"
    },
    "&$expanded": {
      margin: 0
    }
  },
  expanded: {}
});

const AccordionDetails = styled(MuiAccordionDetails)({
  root: {
    display: "block"
  }
});

const navigationDrawerLinkStyle = {
  margin: "0 8px"
};

const isCategoriesExpandedDefault = true;
const isDetailLevelsExpandedDefault = true;

const NavigationDrawerButtons = ({
  selectedCategoryPath,
  selectedDataset,
  title,
  nodes,
  catalog,
  node,
  hub,
  filteredCatalog,
  defaultLanguage,
  languages,
  moduleConfigs,
  DrawerIcon,
  drawerButtonProps,
  additionalCustomPages
}) => {
  const {t} = useTranslation();
  const language = useSelector(languageSelector);
  const nodeCode = useSelector(nodeCodeSelector);

  const [isNavigationDrawerOpen, setisNavigationDrawerOpen] = useState(false);
  const [isCategoriesExpanded, setisCategoriesExpanded] = useState(isCategoriesExpandedDefault);
  const [isDetailLevelsExpanded, setisDetailLevelsExpanded] = useState(isDetailLevelsExpandedDefault);

  const onNavigationDrawerOpen = useCallback(() => {
    setisNavigationDrawerOpen(true);
    setisCategoriesExpanded(isCategoriesExpandedDefault);
    setisDetailLevelsExpanded(isDetailLevelsExpandedDefault);
  }, []);

  const onNavigationDrawerClose = useCallback(() => {
    setisNavigationDrawerOpen(false);
    setisCategoriesExpanded(isCategoriesExpandedDefault);
    setisDetailLevelsExpanded(isDetailLevelsExpandedDefault);
  }, []);

  const handleCategoriesExpand = useCallback(() => {
    setisCategoriesExpanded(prevVal => !prevVal);
  }, []);

  const handleDetailLevelsExpand = useCallback(() => {
    setisDetailLevelsExpanded(prevVal => !prevVal);
  }, []);

  const nodeSidenavCustomPages = getCustomPagesInMenu(node?.itemContainers?.customPages, ConfigNodeMenuId.SIDENAV);

  const categoriesRef = useRef(null);
  const [categoriesHeight, setCategoriesHeight] = useState(0);

  useEffect(() => {
    if (isNavigationDrawerOpen) {
      const resizeObserver = new ResizeObserver(() => {
        if (categoriesRef.current) {
          const top = categoriesRef.current.getBoundingClientRect().top + 24;
          setCategoriesHeight(`calc(100vh - ${Math.floor(top)}px)`);
        }
      });
      resizeObserver.observe(document.body);
      return () => resizeObserver.disconnect();
    }
  }, [isNavigationDrawerOpen]);

  return (
    <Fragment>
      <Grid className="navigation-drawer-btn">
        <Tooltip title={t("components.header.actions.openNavigationDrawer.title")}>
          <IconButton
            color="inherit"
            onClick={onNavigationDrawerOpen}
            aria-label={t("components.header.actions.openNavigationDrawer.ariaLabel")}
            {...drawerButtonProps}
          >
            {DrawerIcon ? <DrawerIcon /> : <MenuIcon />}
          </IconButton>
        </Tooltip>
      </Grid>
      <Drawer
        anchor="left"
        open={isNavigationDrawerOpen}
        PaperProps={{
          sx: {
            width: "520px",
            maxWidth: "100%"
          }
        }}
        onClose={onNavigationDrawerClose}
      >
        <nav
          id="navigation-drawer"
          aria-label={t("components.header.navigationDrawer.ariaLabel")}
          style={{height: "100vh"}}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 16px"
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: "20px",
                fontWeight: "500px",
                padding: "16px 0"
              }}
            >
              {title || node?.code}
            </Box>
            <Tooltip title={t("components.header.actions.closeNavigationDrawer.title")}>
              <IconButton
                onClick={onNavigationDrawerClose}
                aria-label={t("components.header.actions.closeNavigationDrawer.ariaLabel")}
              >
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Divider sx={{margin: "0 8px 8px"}} />
          <Box
            sx={{
              "& .custom-link__content__icon-and-text__icon": {
                display: "none"
              }
            }}
          >
            {node &&
              (nodes || []).length > 1 &&
              window.location.href.toLowerCase().includes(node.code.toLowerCase()) && (
                <Box id="navigation-drawer-home" className="navigation-drawer-link" sx={navigationDrawerLinkStyle}>
                  <CustomLink
                    to={getNodeInternalUrl(node?.code)}
                    text={t("components.header.navigationDrawer.backToNodeHome")}
                    icon={<HomeIcon />}
                  />
                </Box>
              )}
            <ModulesPlaceholder
              id="navigation-drawer-news-placeholder"
              sx={navigationDrawerLinkStyle}
              moduleFallback={<Box sx={navigationDrawerLinkStyle} style={{height: 40}} />}
            />
            <Box id="navigation-drawer-categories" className="navigation-drawer-link" sx={navigationDrawerLinkStyle}>
              <CustomLink
                to={`/${language}/${(nodeCode || "").toLowerCase()}/categories`}
                text={t("components.header.navigationDrawer.categories")}
                icon={<Topic />}
              />
            </Box>
            <ModulesPlaceholder
              id="navigation-drawer-advanced-search-placeholder"
              sx={navigationDrawerLinkStyle}
              moduleFallback={<Box sx={navigationDrawerLinkStyle} style={{height: 40}} />}
            />
            {showNodeDashboardsButton(moduleConfigs, node, hub) && (
              <Box id="navigation-drawer-dashboards" className="navigation-drawer-link" sx={navigationDrawerLinkStyle}>
                <CustomLink
                  to={getNodeDashboardsInternalUrl(node?.code)}
                  text={t("components.header.navigationDrawer.goToDashboard")}
                  icon={<DashboardIcon />}
                />
              </Box>
            )}
            <ModulesPlaceholder
              id="navigation-drawer-generic-placeholder"
              sx={navigationDrawerLinkStyle}
              moduleFallback={<Box sx={navigationDrawerLinkStyle} />}
            />
            {nodeSidenavCustomPages?.length > 0 &&
              nodeSidenavCustomPages?.map((v, idx) => (
                <Box
                  key={idx}
                  id={`navigation-drawer-custom-page-${v.id}`}
                  className="navigation-drawer-link navigation-drawer-custom-page"
                  sx={navigationDrawerLinkStyle}
                >
                  <CustomLink
                    key={v.id}
                    to={getCustomPageInternalUrl(v.id, node?.code)}
                    text={localizeI18nObj(v.title, defaultLanguage, languages) || ""}
                  />
                </Box>
              ))}
            {additionalCustomPages?.length > 0 &&
              _.differenceBy(additionalCustomPages, nodeSidenavCustomPages, "id")?.map((v, idx) => (
                <Box
                  key={idx}
                  id={`navigation-drawer-custom-page-${v.id}`}
                  className="navigation-drawer-link navigation-drawer-custom-page"
                  sx={navigationDrawerLinkStyle}
                >
                  <CustomLink
                    key={v.id}
                    to={getCustomPageInternalUrl(v.id, node?.code)}
                    text={localizeI18nObj(v.title, defaultLanguage, languages) || ""}
                  />
                </Box>
              ))}
          </Box>
          {catalog && (
            <Fragment>
              <Divider sx={{margin: "12px 8px 8px"}} />
              {catalog.detailLevels.length > 0 && (
                <Accordion
                  expanded={isDetailLevelsExpanded}
                  onChange={handleDetailLevelsExpand}
                  elevation={0}
                  disableGutters
                  sx={{"&:before": {display: "none"}}}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography component="span" sx={{paddingLeft: "4px"}}>
                      {t("components.header.navigationDrawer.detailLevelSelectTitle")}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <DetailLevelSelect variant="outlined" />
                  </AccordionDetails>
                </Accordion>
              )}
              <Accordion
                expanded={isCategoriesExpanded}
                onChange={handleCategoriesExpand}
                elevation={0}
                disableGutters
                sx={{"&:before": {display: "none"}}}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography component="span">{t("components.header.navigationDrawer.categoriesTitle")}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box ref={categoriesRef}>
                    <CategoriesTree
                      selectedCategories={selectedCategoryPath}
                      selectedPackage={selectedDataset}
                      catalog={filteredCatalog}
                      node={node}
                      onClose={onNavigationDrawerClose}
                      height={categoriesHeight}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Fragment>
          )}
        </nav>
      </Drawer>
    </Fragment>
  );
};

export default NavigationDrawerButtons;
